import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';
import type {
  WhatsAppMessage,
  TemplateMessageComponent,
  UseWhatsAppMessagesResult
} from '@/types/whatsapp';

const PAGE_SIZE = 50;

export function useWhatsAppMessages(
  businessId: string | null,
  contactId: string | null
): UseWhatsAppMessagesResult {
  const queryClient = useQueryClient();

  const queryKey = ['whatsapp-messages', businessId, contactId];

  // Fetch messages with infinite scroll
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      if (!businessId || !contactId) return { messages: [], nextPage: null };

      const { data: messages, error } = await supabase
        .from('whatsapp_messages')
        .select('*')
        .eq('contact_id', contactId)
        .order('sent_at', { ascending: false })
        .range(pageParam, pageParam + PAGE_SIZE - 1);

      if (error) throw error;

      return {
        messages: (messages || []) as WhatsAppMessage[],
        nextPage: messages && messages.length === PAGE_SIZE ? pageParam + PAGE_SIZE : null,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!businessId && !!contactId,
    initialPageParam: 0,
  });

  // Flatten and reverse messages (oldest first for display)
  const messages = (data?.pages.flatMap(page => page.messages) || []).reverse();

  // Real-time subscription for messages
  useEffect(() => {
    if (!businessId || !contactId) return;

    const channel = supabase
      .channel(`whatsapp-messages-${contactId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'whatsapp_messages',
          filter: `contact_id=eq.${contactId}`,
        },
        (payload) => {
          // Skip invalidation if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;

          if (payload.eventType === 'INSERT') {
            // Add new message to the list
            queryClient.invalidateQueries({ queryKey });
          } else if (payload.eventType === 'UPDATE') {
            // Update message status
            queryClient.invalidateQueries({ queryKey });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [businessId, contactId, queryClient, queryKey]);

  // Send text message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!businessId || !contactId) throw new Error('No contact selected');

      const response = await supabase.functions.invoke('whatsapp-send', {
        body: {
          business_id: businessId,
          contact_id: contactId,
          content,
          message_type: 'text',
        },
      });

      if (response.error) throw response.error;
      if (response.data?.error) {
        const err = new Error(response.data.error) as Error & { requires_template?: boolean };
        err.requires_template = response.data.requires_template;
        throw err;
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      // Also refresh contacts to update last_message
      queryClient.invalidateQueries({ queryKey: ['whatsapp-contacts', businessId] });
    },
    onError: (error: Error & { requires_template?: boolean }) => {
      if (error.requires_template) {
        toast.error('Template required', {
          description: 'This conversation is outside the 24-hour window. Use a template to continue.',
        });
      } else {
        toast.error('Failed to send message', {
          description: error.message,
        });
      }
    },
  });

  // Send template message mutation
  const sendTemplateMutation = useMutation({
    mutationFn: async ({
      templateName,
      components,
    }: {
      templateName: string;
      components?: TemplateMessageComponent[];
    }) => {
      if (!businessId || !contactId) throw new Error('No contact selected');

      const response = await supabase.functions.invoke('whatsapp-send', {
        body: {
          business_id: businessId,
          contact_id: contactId,
          message_type: 'template',
          template_name: templateName,
          template_language: 'en',
          template_components: components,
        },
      });

      if (response.error) throw response.error;
      if (response.data?.error) throw new Error(response.data.error);

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-contacts', businessId] });
      toast.success('Template sent');
    },
    onError: (error: Error) => {
      toast.error('Failed to send template', {
        description: error.message,
      });
    },
  });

  // Mark messages as read
  const markAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!contactId) return;

      const { error } = await supabase
        .from('whatsapp_messages')
        .update({ read_at: new Date().toISOString() })
        .eq('contact_id', contactId)
        .eq('direction', 'inbound')
        .is('read_at', null);

      if (error) throw error;
    },
    onSuccess: () => {
      // Refresh contacts to update unread count
      queryClient.invalidateQueries({ queryKey: ['whatsapp-contacts', businessId] });
    },
  });

  const sendMessage = useCallback(async (content: string) => {
    await sendMessageMutation.mutateAsync(content);
  }, [sendMessageMutation]);

  const sendTemplate = useCallback(async (
    templateName: string,
    components?: TemplateMessageComponent[]
  ) => {
    await sendTemplateMutation.mutateAsync({ templateName, components });
  }, [sendTemplateMutation]);

  const markAsRead = useCallback(async () => {
    await markAsReadMutation.mutateAsync();
  }, [markAsReadMutation]);

  const loadMore = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  return {
    messages,
    isLoading,
    error: error as Error | null,
    hasMore: !!hasNextPage,
    loadMore,
    sendMessage,
    sendTemplate,
    markAsRead,
    refresh: refetch,
  };
}
